import type {
  CreateImportOrderDTO,
  ImportOrderListQueryDTO,
  ImportOrderTrackingInputDTO,
  LandedCostPreviewInputDTO,
  MarkImportOrderAtCarrierDTO,
  MarkImportOrderShortDTO,
  MatchImportOrderLineDTO,
  ReceiveImportOrderDTO,
  SkipImportOrderLineDTO,
  UpdateImportOrderDTO,
  UpdateImportOrderTrackingDTO,
} from "@/modules/inventory/domain/import-order-contracts";
import type {
  GoodsReceiptDetailDTO,
  ImportOrderInTransitValueSummaryDTO,
  ImportOrderLandedCostPreview,
  ImportOrderOverdueAlertDTO,
  ImportOrderPhase13DetailDTO,
  ImportOrderPhase13ListResult,
} from "@/modules/inventory/domain/types";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

const base = () => `${config.apiUrl}/import-orders`;

function appendListQuery(url: URL, query?: ImportOrderListQueryDTO) {
  if (query?.status) url.searchParams.set("status", query.status);
  if (query?.source) url.searchParams.set("source", query.source);
  if (query?.search) url.searchParams.set("search", query.search);
  if (query?.overdueOnly) url.searchParams.set("overdueOnly", "true");
  if (query?.limit != null) url.searchParams.set("limit", String(query.limit));
  if (query?.offset != null) {
    url.searchParams.set("offset", String(query.offset));
  }
}

export const importOrdersClient = {
  async list(
    query?: ImportOrderListQueryDTO,
  ): Promise<ImportOrderPhase13ListResult> {
    const url = new URL(base());
    appendListQuery(url, query);
    return fetcher.get(url.toString());
  },

  async detail(id: string): Promise<ImportOrderPhase13DetailDTO> {
    return fetcher.get(`${base()}/${id}`);
  },

  async create(
    input: CreateImportOrderDTO,
  ): Promise<ImportOrderPhase13DetailDTO> {
    return fetcher.post(base(), input);
  },

  async update(
    id: string,
    input: UpdateImportOrderDTO,
  ): Promise<ImportOrderPhase13DetailDTO> {
    return fetcher.patch(`${base()}/${id}`, input);
  },

  async markOrdered(id: string): Promise<ImportOrderPhase13DetailDTO> {
    return fetcher.post(`${base()}/${id}/mark-ordered`, {});
  },

  async markAtCarrier(
    id: string,
    input: MarkImportOrderAtCarrierDTO,
  ): Promise<ImportOrderPhase13DetailDTO> {
    return fetcher.post(`${base()}/${id}/mark-at-carrier`, input);
  },

  async addTracking(
    id: string,
    input: ImportOrderTrackingInputDTO,
  ): Promise<ImportOrderPhase13DetailDTO> {
    return fetcher.post(`${base()}/${id}/trackings`, input);
  },

  async updateTracking(
    id: string,
    trackingId: string,
    input: UpdateImportOrderTrackingDTO,
  ): Promise<ImportOrderPhase13DetailDTO> {
    return fetcher.patch(`${base()}/${id}/trackings/${trackingId}`, input);
  },

  async receive(
    id: string,
    input: ReceiveImportOrderDTO,
  ): Promise<{
    importOrder: ImportOrderPhase13DetailDTO;
    goodsReceipt: GoodsReceiptDetailDTO;
  }> {
    return fetcher.post(`${base()}/${id}/receive`, input);
  },

  async voidReceive(
    id: string,
    receiptId: string,
  ): Promise<ImportOrderPhase13DetailDTO> {
    return fetcher.post(`${base()}/${id}/receipts/${receiptId}/void`, {});
  },

  async voidGoodsReceipt(
    id: string,
    goodsReceiptId: string,
  ): Promise<ImportOrderPhase13DetailDTO> {
    return fetcher.post(
      `${base()}/${id}/goods-receipts/${goodsReceiptId}/void`,
      {},
    );
  },

  async reverseReceive(id: string): Promise<ImportOrderPhase13DetailDTO> {
    return fetcher.post(`${base()}/${id}/reverse-receive`, {});
  },

  async markShort(
    id: string,
    input: MarkImportOrderShortDTO,
  ): Promise<ImportOrderPhase13DetailDTO> {
    return fetcher.post(`${base()}/${id}/mark-short`, input);
  },

  async cancel(id: string): Promise<ImportOrderPhase13DetailDTO> {
    return fetcher.post(`${base()}/${id}/cancel`, {});
  },

  async previewLandedCost(
    input: LandedCostPreviewInputDTO,
  ): Promise<ImportOrderLandedCostPreview> {
    return fetcher.post(`${base()}/preview-landed-cost`, input);
  },

  async landedCostPreviewById(
    id: string,
  ): Promise<ImportOrderLandedCostPreview> {
    return fetcher.get(`${base()}/${id}/landed-cost-preview`);
  },

  async summary(): Promise<ImportOrderInTransitValueSummaryDTO> {
    return fetcher.get(`${base()}/summary/in-transit-value`);
  },

  async overdue(): Promise<{ data: ImportOrderOverdueAlertDTO[] }> {
    return fetcher.get(`${base()}/alerts/overdue`);
  },

  async incomingByVariants(variantIds: string[]): Promise<{
    data: Array<{
      variantId: string;
      incomingQuantity: number;
      inTransitValueLak: string;
    }>;
  }> {
    const url = new URL(`${base()}/incoming/by-variants`);
    url.searchParams.set("variantIds", variantIds.join(","));
    return fetcher.get(url.toString());
  },

  async lastPrices(variantIds: string[]) {
    const url = new URL(`${base()}/last-prices`);
    url.searchParams.set("variantIds", variantIds.join(","));
    return fetcher.get<{
      data: Array<{
        variantId: string;
        unitPriceForeign: string | null;
        source: string | null;
        orderedAt: string | null;
      }>;
    }>(url.toString());
  },

  async variantPriceHistory(variantId: string) {
    return fetcher.get<{
      data: Array<{
        importOrderId: string;
        externalOrderNo: string;
        source: string;
        orderedAt: string | null;
        unitPriceForeign: string;
        currency: string;
        exchangeRate: string;
        unitLandedCostLak: string;
        orderedQuantity: number;
      }>;
    }>(`${base()}/variants/${variantId}/price-history`);
  },

  async exchangeSettings() {
    return fetcher.get<{
      defaultExchangeRate: number;
      lastExchangeRate: number | null;
    }>(`${base()}/settings/exchange-rate`);
  },

  async arrivalEstimates() {
    return fetcher.get<{
      bySource: Array<{
        source: string;
        medianLeadDays: number;
        sampleCount: number;
        isFallback: boolean;
      }>;
    }>(`${base()}/settings/arrival-estimates`);
  },

  async updateExchangeSettings(input: { defaultExchangeRate: number }) {
    return fetcher.patch(`${base()}/settings/exchange-rate`, input);
  },

  async exportCsv(query?: ImportOrderListQueryDTO): Promise<Blob> {
    const url = new URL(`${base()}/export`);
    appendListQuery(url, query);
    const res = await fetch(url.toString(), { credentials: "include" });
    if (!res.ok) throw new Error("EXPORT_FAILED");
    return res.blob();
  },

  async inTransitByOrder(query?: {
    source?: string;
    status?: string;
    overdueOnly?: boolean;
  }) {
    const url = new URL(`${base()}/reports/in-transit/by-order`);
    if (query?.source) url.searchParams.set("source", query.source);
    if (query?.status) url.searchParams.set("status", query.status);
    if (query?.overdueOnly) url.searchParams.set("overdueOnly", "true");
    return fetcher.get(url.toString());
  },

  async inTransitByVariant(query?: {
    source?: string;
    status?: string;
    overdueOnly?: boolean;
  }) {
    const url = new URL(`${base()}/reports/in-transit/by-variant`);
    if (query?.source) url.searchParams.set("source", query.source);
    if (query?.status) url.searchParams.set("status", query.status);
    if (query?.overdueOnly) url.searchParams.set("overdueOnly", "true");
    return fetcher.get(url.toString());
  },

  async matchLine(
    importOrderId: string,
    lineId: string,
    input: MatchImportOrderLineDTO,
  ): Promise<ImportOrderPhase13DetailDTO> {
    return fetcher.post(
      `${base()}/${importOrderId}/lines/${lineId}/match`,
      input,
    );
  },

  async skipLine(
    importOrderId: string,
    lineId: string,
    input: SkipImportOrderLineDTO,
  ): Promise<ImportOrderPhase13DetailDTO> {
    return fetcher.post(
      `${base()}/${importOrderId}/lines/${lineId}/skip`,
      input,
    );
  },
};
